import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { PageHeader, SectionCard, EmptyState } from '../ui';
import { paymentService, type BotAccessUser } from '../../services/paymentService';

type Filter = 'pending' | 'allowed' | 'blocked';
type Status = 'pending' | 'allowed' | 'blocked';

const STATUS_COLOR: Record<Status, 'warning' | 'success' | 'default'> = {
  pending: 'warning',
  allowed: 'success',
  blocked: 'default',
};

const fmt = (ms: number) => (ms ? new Date(ms).toLocaleString() : '—');
/** Hiển thị point: bỏ số 0 lẻ thừa (vd 4.99, 10, 0). */
const fmtPts = (n?: number) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

export const BotUsers: React.FC = () => {
  const [filter, setFilter] = useState<Filter>('pending');
  const [rows, setRows] = useState<BotAccessUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newId, setNewId] = useState('');
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const notify = (msg: string, sev: 'success' | 'error') => setToast({ msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await paymentService.listBotAccess(filter)).users);
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (telegramId: string, status: Status) => {
    setBusy(true);
    try {
      await paymentService.setBotAccess(telegramId, status);
      notify(`Đã cập nhật ${telegramId} → ${status}.`, 'success');
      await load();
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const addAllowed = async () => {
    const id = newId.trim();
    if (!/^\d{1,20}$/.test(id)) return notify('Telegram ID phải là chuỗi số.', 'error');
    await setStatus(id, 'allowed');
    setNewId('');
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="Bot Users" description="Allowlist người dùng bot Telegram. Duyệt danh sách chờ hoặc chặn." />

      <SectionCard title="Thêm vào allowlist">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Telegram ID"
            value={newId}
            onChange={(e) => setNewId(e.target.value)}
            size="small"
            sx={{ maxWidth: 240 }}
          />
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={addAllowed} disabled={busy}>
            Cấp phép
          </Button>
        </Stack>
      </SectionCard>

      <Box sx={{ mt: 3 }}>
        <Tabs value={filter} onChange={(_, v) => setFilter(v)} sx={{ mb: 1 }}>
          <Tab label="Chờ duyệt" value="pending" />
          <Tab label="Đã cấp phép" value="allowed" />
          <Tab label="Bị chặn" value="blocked" />
        </Tabs>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : rows.length === 0 ? (
          <EmptyState title="Không có người dùng nào" />
        ) : (
          <TableContainer component={Box}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Telegram ID</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Tên</TableCell>
                  <TableCell align="right">Đã nạp (còn lại)</TableCell>
                  <TableCell>Yêu cầu lúc</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell align="right">Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((u) => (
                  <TableRow key={u.telegramId}>
                    <TableCell>{u.telegramId}</TableCell>
                    <TableCell>{u.username ? `@${u.username}` : '—'}</TableCell>
                    <TableCell>{u.firstName || '—'}</TableCell>
                    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                      {fmtPts(u.deposited)} <span style={{ opacity: 0.6 }}>({fmtPts(u.balance)})</span>
                    </TableCell>
                    <TableCell>{fmt(u.requestedAt)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip size="small" label={u.status} color={STATUS_COLOR[u.status]} />
                        {u.hasApiKey && <Chip size="small" label="API" color="info" variant="outlined" />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {u.status !== 'allowed' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<CheckCircleOutlineIcon />}
                            disabled={busy}
                            onClick={() => setStatus(u.telegramId, 'allowed')}
                          >
                            Cấp phép
                          </Button>
                        )}
                        {u.status !== 'blocked' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<BlockIcon />}
                            disabled={busy}
                            onClick={() => setStatus(u.telegramId, 'blocked')}
                          >
                            Chặn
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
